import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles } from 'lucide-react';
import './VibeLevelModal.css';
import { getLocalCurrentDateTime } from '../../utils/dates';

type VibeLevel =
  | 'quiet'
  | 'dull'
  | 'chill'
  | 'family'
  | 'energetic'
  | 'social'
  | 'lively'
  | 'party'
  | 'romantic'
  | 'trendy';

interface VibeLevelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (level: VibeLevel) => void;
  latitude?: number; // optional to compute local time
  longitude?: number;
}

const dayVibes: Array<{ id: VibeLevel; label: string; gradient: string }> = [
  { id: 'quiet', label: 'Quiet', gradient: 'linear-gradient(135deg, #9ca3af, #6b7280)' },
  { id: 'dull', label: 'Dull', gradient: 'linear-gradient(135deg, #a1a1aa, #71717a)' },
  { id: 'chill', label: 'Chill', gradient: 'linear-gradient(135deg, #38bdf8, #0ea5e9)' },
  { id: 'family', label: 'Family', gradient: 'linear-gradient(135deg, #10b981, #059669)' },
  { id: 'energetic', label: 'Energetic', gradient: 'linear-gradient(135deg, #f59e0b, #d97706)' },
  { id: 'social', label: 'Social', gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' },
  { id: 'trendy', label: 'Trendy', gradient: 'linear-gradient(135deg, #ec4899, #db2777)' },
];

const nightVibes: Array<{ id: VibeLevel; label: string; gradient: string }> = [
  { id: 'quiet', label: 'Quiet', gradient: 'linear-gradient(135deg, #9ca3af, #6b7280)' },
  { id: 'dull', label: 'Dull', gradient: 'linear-gradient(135deg, #a1a1aa, #71717a)' },
  { id: 'chill', label: 'Laid-back', gradient: 'linear-gradient(135deg, #38bdf8, #0ea5e9)' },
  { id: 'romantic', label: 'Romantic', gradient: 'linear-gradient(135deg, #f472b6, #ec4899)' },
  { id: 'social', label: 'Social', gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' },
  { id: 'lively', label: 'Lively', gradient: 'linear-gradient(135deg, #f59e0b, #d97706)' },
  { id: 'party', label: 'Party', gradient: 'linear-gradient(135deg, #ef4444, #dc2626)' },
  { id: 'trendy', label: 'Trendy', gradient: 'linear-gradient(135deg, #22d3ee, #06b6d4)' },
];

const VibeLevelModal: React.FC<VibeLevelModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  latitude = -33.86,
  longitude = 151.21,
}) => {
  const [selected, setSelected] = React.useState<VibeLevel | null>(null);
  const [isFilling, setIsFilling] = React.useState(false);
  const [fillProgress, setFillProgress] = React.useState(0);
  const fillTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const progressIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = React.useRef<number | null>(null);

  // Lock body scroll when modal is open
  React.useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        const scrollY = document.body.style.top;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      };
    }
  }, [isOpen]);

  // Determine local hour for day/night using existing helper
  const local = getLocalCurrentDateTime(latitude, longitude);
  const hour = Number(local.dateTime.split('T')[1].slice(0, 2));
  const isNight = hour >= 18 || hour < 6;
  const options = isNight ? nightVibes : dayVibes;

  const handleRecommend = () => {
    if (!selected) return;

    // If already filling, submit immediately
    if (isFilling) {
      if (fillTimeoutRef.current) clearTimeout(fillTimeoutRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      setIsFilling(false);
      setFillProgress(0);
      onSelect(selected);
      return;
    }
  };

  // Start fill animation when a vibe level is selected
  React.useEffect(() => {
    if (!selected || !isOpen) {
      return;
    }

    // Clear any existing timers first
    if (fillTimeoutRef.current) clearTimeout(fillTimeoutRef.current);
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);

    // Small delay to ensure UI is ready
    const initTimer = setTimeout(() => {
      // Start fill animation
      setIsFilling(true);
      setFillProgress(0);
      startTimeRef.current = Date.now();

      // Update progress every 16ms (60fps)
      progressIntervalRef.current = setInterval(() => {
        if (startTimeRef.current) {
          const elapsed = Date.now() - startTimeRef.current;
          const progress = Math.min((elapsed / 2000) * 100, 100);
          setFillProgress(progress);

          if (progress >= 100) {
            if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
          }
        }
      }, 16);

      // Auto-submit after 2 seconds
      fillTimeoutRef.current = setTimeout(() => {
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        setIsFilling(false);
        setFillProgress(0);
        onSelect(selected);
      }, 2000);
    }, 50);

    return () => {
      clearTimeout(initTimer);
      if (fillTimeoutRef.current) clearTimeout(fillTimeoutRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, [selected, isOpen, onSelect]);

  // Reset when selection is cleared
  React.useEffect(() => {
    if (!selected) {
      setIsFilling(false);
      setFillProgress(0);
      if (fillTimeoutRef.current) clearTimeout(fillTimeoutRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    }
  }, [selected]);

  // Cleanup on unmount or close
  React.useEffect(() => {
    return () => {
      if (fillTimeoutRef.current) clearTimeout(fillTimeoutRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, []);

  // Reset when modal opens or closes - ensure no preselection
  React.useEffect(() => {
    if (isOpen) {
      // Reset selection when modal opens to ensure no preselection
      setSelected(null);
      setIsFilling(false);
      setFillProgress(0);
      if (fillTimeoutRef.current) clearTimeout(fillTimeoutRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    } else {
      // Also reset on close
      setSelected(null);
      setIsFilling(false);
      setFillProgress(0);
      if (fillTimeoutRef.current) clearTimeout(fillTimeoutRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="recommendations-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="vibe-modal"
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="vibe-modal-header">
            <h2>How's the vibe?</h2>
            <button onClick={onClose} className="close-button">
              <X size={24} />
            </button>
          </div>

          <div className="vibe-options">
            {options.map((vibe) => (
              <motion.button
                key={vibe.id}
                className={`vibe-option ${selected === vibe.id ? 'selected' : ''}`}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setSelected(vibe.id)}
              >
                <div className="vibe-icon-circle" style={{ background: vibe.gradient }}>
                  <Sparkles size={20} />
                </div>
                <div className="vibe-option-title">{vibe.label}</div>
              </motion.button>
            ))}
          </div>

          <div className="vibe-actions">
            <button className="action-button secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              className={`action-button primary ${isFilling ? 'filling' : ''}`}
              disabled={!selected}
              onClick={handleRecommend}
            >
              <span className="button-content">Recommend</span>
              {isFilling && <div className="fill-progress" style={{ width: `${fillProgress}%` }} />}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default VibeLevelModal;
